import { useState } from "react";
import { can } from "@/lib/rbac";
import { MACHINE_STATUS } from "@/lib/constants";
import { OperatorChips } from "@/components/OperatorChips";
import { DowntimeLogList } from "@/components/machines/DowntimeLogList";
import { DowntimeFormDialog } from "@/components/machines/DowntimeFormDialog";
import { MachineFormDialog } from "@/components/machines/MachineFormDialog";
import { AssignOperatorsDialog } from "@/components/machines/AssignOperatorsDialog";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DT } from "@/components/ui/denseTable";
import { Plus, Cpu, Trash2, Activity, Wrench, UserPlus } from "lucide-react";

const utilColor = (util) => (util > 90 ? "#EF4444" : util > 70 ? "#F59E0B" : "#2563EB");

function MachineRow({ machine: m, orders, shiftHours, admin, canToggle, onDelete, onLogDowntime, onAssign, onUpdate }) {
  const st = MACHINE_STATUS[m.status] || MACHINE_STATUS.idle;
  const load = orders.filter((o) => o.line_id === m.id && o.status !== "Completed").reduce((s, o) => s + (o.target_quantity || 0), 0);
  const cap = (m.max_hourly_capacity || 1) * (shiftHours || 8);
  const util = Math.min(100, Math.round((load / cap) * 100));
  return (
    <tr className={DT.row} data-testid={`machine-row-${m.line_code}`}>
      <td className={`${DT.td} ${DT.stickyLeft} min-w-[180px]`}>
        <span className="flex items-center gap-2">
          <span className="h-8 w-8 rounded-md bg-slate-100 flex items-center justify-center text-slate-600 shrink-0"><Cpu className="h-4 w-4" /></span>
          <span className="min-w-0">
            <p className="text-xs font-semibold text-slate-800 font-mono-data truncate">{m.line_code}</p>
            <p className="text-[11px] text-slate-400 truncate">{m.name}</p>
          </span>
        </span>
      </td>
      <td className={DT.tdRight}>{m.max_hourly_capacity} <span className="text-slate-400">u/hr</span></td>
      <td className={DT.td}>
        <div className="flex items-center gap-2 min-w-[120px]">
          <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${util}%`, background: utilColor(util) }} /></div>
          <span className="text-[11px] font-mono-data text-slate-600 whitespace-nowrap">{util}%</span>
        </div>
        <p className="text-[10px] text-slate-400 font-mono-data mt-0.5">{m.assigned_orders || 0} orders</p>
      </td>
      <td className={DT.td}>
        <div className="flex items-center gap-2" data-testid={`machine-operators-${m.line_code}`}>
          <OperatorChips operators={m.operators} max={3} emptyLabel="None" className="text-[11px]" />
          {admin && (
            <button type="button" onClick={() => onAssign(m)} className="shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-800" data-testid={`assign-operators-${m.line_code}`}>
              <UserPlus className="h-3 w-3" /> Assign
            </button>
          )}
        </div>
      </td>
      <td className={DT.td}>
        <span className={`inline-flex items-center gap-1 text-[10px] font-mono-data uppercase tracking-wide px-2 py-1 rounded ${st.bg}`} data-testid={`machine-status-badge-${m.line_code}`}>
          <Activity className="h-3 w-3" /> {st.label}{m.auto_downtime ? " · auto" : ""}
        </span>
        {m.auto_downtime && <p className="text-[10px] text-red-500 italic mt-0.5" data-testid={`auto-downtime-note-${m.line_code}`}>Auto window — reverts when it ends</p>}
      </td>
      <td className={`${DT.td} ${DT.stickyRight}`}>
        <div className={DT.actionsCell}>
          {canToggle && (
            <Select value={m.status} onValueChange={(v) => onUpdate(m, { status: v })}>
              <SelectTrigger className="h-7 w-24 text-xs" data-testid="machine-status-toggle"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="idle">Idle</SelectItem>
                <SelectItem value="downtime">Downtime</SelectItem>
              </SelectContent>
            </Select>
          )}
          {admin && (
            <button onClick={() => onLogDowntime(m)} className={`${DT.iconBtn} hover:text-amber-600`} title="Log downtime" data-testid={`log-downtime-${m.line_code}`}>
              <Wrench className="h-3.5 w-3.5" />
            </button>
          )}
          {admin && (
            <button onClick={() => onDelete(m)} className={`${DT.iconBtn} hover:text-red-600`} title="Delete line" data-testid={`delete-machine-${m.line_code}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function MachinesPanel({ machines, orders, role, downtimes = [], operators = [], shiftHours = 8, onCreate, onUpdate, onDelete, onLogDowntime, onDeleteDowntime }) {
  const [addOpen, setAddOpen] = useState(false);
  const [dtMachine, setDtMachine] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const admin = can.manageMachines(role);
  const canToggle = can.toggleMachineStatus(role);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="machine-resource-grid">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-base font-semibold text-slate-900">Lines &amp; Machines</h3>
          <p className="text-xs text-slate-400">Capacity, utilisation, operational status &amp; assigned operators</p>
        </div>
        {admin && (
          <Button size="sm" onClick={() => setAddOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-add-machine">
            <Plus className="h-4 w-4 mr-1" /> Add Line
          </Button>
        )}
      </div>

      {machines.length === 0 ? (
        <p className="py-8 text-center text-sm text-slate-400 italic" data-testid="machines-empty">No lines to show.</p>
      ) : (
        <div className={DT.wrap}>
          <table className={`${DT.table} min-w-[820px]`}>
            <thead className={DT.thead}>
              <tr className={DT.headerRow}>
                <th className={`${DT.th} ${DT.stickyLeft}`}>Line</th>
                <th className={DT.thRight}>Max Capacity</th>
                <th className={DT.th}>Utilisation</th>
                <th className={DT.th}>Operators</th>
                <th className={DT.th}>Status</th>
                <th className={`${DT.thRight} ${DT.stickyRight}`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {machines.map((m) => (
                <MachineRow key={m.id} machine={m} orders={orders} shiftHours={shiftHours} admin={admin} canToggle={canToggle}
                  onUpdate={onUpdate} onDelete={onDelete} onLogDowntime={setDtMachine} onAssign={setAssignFor} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DowntimeLogList downtimes={downtimes} canDelete={admin} onDelete={onDeleteDowntime} />

      {dtMachine && <DowntimeFormDialog machine={dtMachine} onClose={() => setDtMachine(null)} onSubmit={onLogDowntime} />}
      {assignFor && (
        <AssignOperatorsDialog machine={assignFor} operators={operators} machines={machines} onClose={() => setAssignFor(null)}
          onSave={(m, operator_ids) => onUpdate(m, { operator_ids })} />
      )}
      <MachineFormDialog open={addOpen} onOpenChange={setAddOpen} onCreate={onCreate} />
    </div>
  );
}
